import { FavoritesQueryKeys } from '@enums/react-query-keys';
import { useQuery } from '@tanstack/react-query';
import { usePreferences } from '@context/preferences.context';
import { mapCountryToTheSizeType } from '@helpers/utils/catalog/catalog-url-helpers';
import ContentStackService, { CmsUserInfo } from '@services/content-stack-service';
import { ContentStackPageNames } from '@enums/contentStack';
import { User } from '@interfaces/models/user';

const fetchFavoritePageData = async (userId: User['id'], locales: CmsUserInfo) => {
  const response = await ContentStackService.getDataForCMSPage(ContentStackPageNames.FAVORITE_PAGE, {
    ...locales,
    userId,
  });
  return response;
};
const useFavouritesCMSQuery = ({
  userId,
  shouldFetchCMSQuery,
}: {
  userId: User['id'];
  shouldFetchCMSQuery: boolean;
}) => {
  const { country, currency, language, regionName, siteId, locale } = usePreferences(); // Fetch FAVORITES_CMS data
  const {
    data: favoritePageData,
    isFetching: isFetchingFavoritePage,
    error: cmsError,
  } = useQuery({
    queryKey: [FavoritesQueryKeys.FAVORITES_CMS_DATA, userId],
    queryFn: () =>
      fetchFavoritePageData(userId, {
        locale,
        country,
        language,
        currency,
        siteId,
        sizeType: mapCountryToTheSizeType(regionName),
      }),
    enabled: shouldFetchCMSQuery,
    retry: false,
  });

  const transformedFavoritePageData = {
    ...favoritePageData,
    ctaText: favoritePageData?.cta?.text,
    cta: {
      ...favoritePageData?.cta,
      title: favoritePageData?.cta?.text,
      href: favoritePageData?.cta?.url,
      url: favoritePageData?.cta?.url,
    },
  };

  return {
    favoritePageData: transformedFavoritePageData,
    isCMSDataLoading: isFetchingFavoritePage,
    cmsError,
  };
};

export default useFavouritesCMSQuery;
